import {HttpContext, HttpContextToken, HttpErrorResponse} from '@angular/common/http';

/**
 * Marks a request whose failures the caller reports itself.
 *
 * errorInterceptor toasts every failed request, which is right for the calls nobody is handling
 * but wrong twice over for the ones that are: the operator gets the component's message stacked
 * under a raw "Request failed - 404 Not Found", and for lookups where "not found" is an ordinary
 * answer that generic toast is pure noise.
 */
export const SUPPRESS_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

export function silentErrors(): HttpContext {
  return new HttpContext().set(SUPPRESS_ERROR_TOAST, true);
}

/**
 * How this API reports a failure, in one place: a string body, or an object with a `message`.
 *
 * Used by errorInterceptor for the toast and by the pages that opt out of it, so the two cannot
 * drift into describing the same failure differently.
 */
/**
 * The `HttpErrorResponse` inside whatever a failed `rxResource` handed back, if there is one.
 *
 * Needed because `HttpErrorResponse` only *implements* `Error` -- it does not extend it -- so
 * `resource.error()` never returns one directly: Angular sees a non-`Error` and wraps it in a
 * `ResourceWrappedError`, whose `.cause` is the response. Reading `.message` off the wrapper gets
 * "Resource returned an error that's not an Error instance: ...", never the API's own message, so
 * anything rendering a resource failure has to come through here first.
 */
export function httpErrorFrom(error: unknown): HttpErrorResponse | undefined {
  if (error instanceof HttpErrorResponse) {
    return error;
  }
  if (error instanceof Error && error.cause instanceof HttpErrorResponse) {
    return error.cause;
  }
  return undefined;
}

export function describeHttpError(error: HttpErrorResponse): string {
  const body: unknown = error.error;
  if (typeof body === 'string' && body.length > 0) {
    return body;
  }
  if (body !== null && typeof body === 'object' && 'message' in body && typeof body.message === 'string') {
    return body.message;
  }
  return error.message;
}
